import React, {FC, useEffect} from "react";
import {T, useTranslate, UseTranslateResult} from "@tolgee/react";
import {useSelector} from "react-redux";
import {Elements} from "@stripe/react-stripe-js";
import Stepper, {Step, StepperImageContainer} from "view/components/Stepper";
import FfairPaper from "view/components/FfairPaper";
import {PaymentProgress} from "store/exhibitors/cartStepper/helper";
import CheckIcon from "view/images/CheckIcon";
import {
  selectBillingDetails,
  selectCartStepperStatus,
  selectTotalAmount,
} from "store/exhibitors/cartStepper/slice";
import {useExhibitor, useStripeClientSecret} from "hooks";
import {FullProduct} from "ts/api/product";

import {PaymentMethodType} from "ts/api/paymentMethod";
import {ExpandedOrder} from "ts/api/order";
import useCurrentExhibitorPairId from "hooks/useCurrentExhibitorPairId";
import useCurrentExhibitorPair from "hooks/useCurrentExhibitorPair";
import {Box} from "@mui/material";
import {useQuickOrderId} from "hooks/show/order";
import {useOrganisers} from "store/organisers/organiser.store";
import {apiV2} from "api/firebase";
import {IShow} from "@evennt-tech/api-v2-core";
import Details from "../Details";
import Payment from "../Payment";
import {useStyles} from "../style";
import useOrderProducts from "../useOrderProducts";
import createOrders from "../../helpers/createOrders";
import {PlaceOrderCallback} from "../types";
import getOrderTransferDescription from "../../helpers/getOrderTransferDescription";

type TranslateFn = UseTranslateResult["t"];

const createCartDetailsSteps = (
  detailsProgress: PaymentProgress,
  t: TranslateFn
): Step[] => [
  {
    label: t("exhibitor.cart.checkout.billing_details_step"),
    icon:
      detailsProgress === PaymentProgress.CARD_DETAILS ? (
        <StepperImageContainer small completed icon={<CheckIcon />} />
      ) : undefined,
  },
  {label: t("exhibitor.cart.checkout.payment_method_step")},
];

type CheckoutProps = {
  show: IShow;
  paymentMethodType: PaymentMethodType;
  changePaymentMethodType: (type: PaymentMethodType) => void;
  showProducts?: FullProduct[];
  orderProducts?: FullProduct[];
  order?: ExpandedOrder;
  categoryIdsBySupplier?: Record<string, Set<string>>;
  isBankTransferAllowedByOrder?: boolean;
};

const Checkout: FC<CheckoutProps> = ({
  show,
  paymentMethodType,
  changePaymentMethodType,
  showProducts,
  orderProducts,
  order,
  categoryIdsBySupplier = {},
  isBankTransferAllowedByOrder = false,
}) => {
  const quickOrderId = useQuickOrderId();
  const {t} = useTranslate();
  const classes = useStyles();
  const {detailsProgress} = useSelector(selectCartStepperStatus);
  const totalAmount = useSelector(selectTotalAmount);

  const products = useOrderProducts({showProducts, orderProducts, order});
  const billingDetails = useSelector(selectBillingDetails);

  const exhibitorPairId = useCurrentExhibitorPairId();

  const [exhibitorPair, connection] = useCurrentExhibitorPair({
    showId: show.id,
    exhibitorPairId,
  });

  const exhibitor = useExhibitor();

  const {stripe: ffairStripe, loading, error} = useStripeClientSecret(
    show.currency
  );

  const {selected: organiser, fetchOne} = useOrganisers();

  useEffect(() => {
    if (show?.organiserOwnerId) {
      fetchOne({organiserId: show?.organiserOwnerId});
    }
  }, [show?.organiserOwnerId]);

  if (!exhibitor || !show || !organiser) {
    return null;
  }

  if (loading) {
    return (
      <Box>
        <T keyName="exhibitor.cart.checkout.loading_payment_form" />
      </Box>
    );
  }

  if (error || !ffairStripe) {
    return (
      <Box>
        <T keyName="exhibitor.cart.checkout.stripe_init_failed" />
      </Box>
    );
  }

  if (
    !exhibitorPair ||
    !exhibitor ||
    !exhibitorPair.stand ||
    !show ||
    !organiser ||
    !connection
  ) {
    return null;
  }

  const renderStepperContent = () => {
    const handlePlaceOrder: PlaceOrderCallback = async ({
      poNumbers,
      isDraft,
      billingMetadata,
    }) => {
      if (!organiser || !exhibitorPair?.stand) {
        return;
      }
      const companyName = exhibitorPair?.companyToShow?.company?.name || "";
      const transferredPaymentDescription = getOrderTransferDescription(
        show.name,
        companyName,
        exhibitorPair.stand.name
      );

      await apiV2.order.saveBillingInfo({
        showId: show.id,
        exhibitorPairId: exhibitorPair.id,
        ...(billingDetails.contactPhoneNumber && {
          contactNumber: String(billingDetails.contactPhoneNumber),
        }),
        ...(billingDetails.vat && {
          vat: billingDetails.vat,
        }),

        ...(billingDetails.tradingName && {
          tradingName: billingDetails.tradingName,
        }),
        ...(billingDetails.name && {
          registeredName: billingDetails.name,
        }),
        ...(billingDetails.contact && {
          contactNameOrderOverride: billingDetails.contact,
        }),
        ...(billingDetails.address && {
          address: billingDetails.address,
        }),
        ...(billingDetails.postCode && {
          postcode: billingDetails.postCode,
        }),
      });

      return createOrders({
        exhibitorId: connection.exhibitorUserId,
        products,
        standId: exhibitorPair.stand.standId,
        exhibitorPairId,
        billingDetails,
        totalAmount,
        show,
        organiser,
        transferredPaymentDescription,
        poNumbers,
        paymentMethodType,
        isDraft,
        quickOrderId,
        billingMetadata,
        allowBankTransfer: isBankTransferAllowedByOrder,
      });
    };

    switch (detailsProgress) {
      case PaymentProgress.CARD_DETAILS:
        return (
          <Elements stripe={ffairStripe}>
            <Payment
              organiser={organiser}
              exhibitorPair={exhibitorPair}
              paymentMethodType={paymentMethodType}
              changePaymentMethodType={changePaymentMethodType}
              products={products}
              show={show}
              onPlaceOrder={handlePlaceOrder}
              isBankTransferAllowedByOrder={isBankTransferAllowedByOrder}
            />
          </Elements>
        );
      default:
        return (
          <Details
            show={show}
            exhibitorPair={exhibitorPair}
            exhibitorUser={exhibitor}
            categoryIdsBySupplier={categoryIdsBySupplier}
          />
        );
    }
  };

  return (
    <FfairPaper className={classes.paper}>
      <Stepper
        orientation="vertical"
        activeStep={detailsProgress}
        steps={createCartDetailsSteps(detailsProgress, t)}
        customLabelClass={classes.verticalLabel}
        content={renderStepperContent()}
      />
    </FfairPaper>
  );
};

export default Checkout;
